// LO Ads Dashboard page (ALA-577). A read-only, single-campaign, SPEND-FREE
// view built on the same-origin T4 proxy (GET /los/ads/dashboard, ALA-575).
// One enrollment check on load: 404 -> a friendly not-enrolled state (the Ads
// tab is also hidden by HomePage); any upstream error -> "temporarily
// unavailable". No spend/CPC/CPL anywhere — the proxy never sends it and this
// page never asks for or renders it (design D5). Per the revised design
// (AlaudaAI/contexts #18) there are also no conversions (D7).
//
// The date range control (Google Ads style presets + custom calendar) is
// CLIENT-SIDE ONLY: it slices the 30-day daily series the single response
// already carries, re-sums the KPI tiles, and redraws the trend. The request
// shape never changes (still the one JWT-only GET, pinned in
// ads-client.test.ts). The M2 ads level renders below as AdsSection, a
// Google Ads style table whose numbers stay pinned to the fixed 30-day
// window — the campaign tiles stay the authoritative totals (D9).

import React, { useEffect, useState } from "react";
import { Row, Col, Spin, Popover, DatePicker } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

import "./Ads.css";
import { loadAdsDashboard } from "./ads-client";
import AdsSection from "./AdsSection";
import {
  interpretAdsResponse,
  freshnessLine,
  deriveRangeView,
  presetDateRange,
  ADS_RANGE_PRESETS,
  type AdsViewState,
  type AdsKpis,
  type AdsTrendPoint,
  type AdsDateRange,
  type AdsRangePresetKey,
} from "./ads-model";

type AdsRangeSelection =
  | { kind: "preset"; key: AdsRangePresetKey }
  | { kind: "custom"; range: AdsDateRange };

function formatCount(n: number): string {
  return Number.isFinite(n) ? Math.round(n).toLocaleString() : "N/A";
}

function formatPercent(v: number | null): string {
  return v == null || !Number.isFinite(v) ? "N/A" : `${(v * 100).toFixed(2)}%`;
}

function shortDate(iso: string): string {
  const [, m, d] = iso.split("-");
  const months = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[Number(m)] ?? m} ${Number(d)}`;
}

/**
 * "Jun 16 – Jul 15, 2026" (en dash), a single "Jul 15, 2026" day, or, when
 * the range spans a year boundary, both years: "Dec 20, 2025 – Jan 18, 2026".
 */
function formatRangeText(range: AdsDateRange): string {
  const fromYear = range.from.slice(0, 4);
  const toYear = range.to.slice(0, 4);
  if (range.from === range.to) return `${shortDate(range.to)}, ${toYear}`;
  if (fromYear !== toYear) {
    return `${shortDate(range.from)}, ${fromYear} – ${shortDate(range.to)}, ${toYear}`;
  }
  return `${shortDate(range.from)} – ${shortDate(range.to)}, ${toYear}`;
}

function localTodayISO(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="ads-kpi">
      <div className="ads-kpi-label">{label}</div>
      <div className="ads-kpi-value">{value}</div>
    </div>
  );
}

function KpiTiles({ kpis }: { kpis: AdsKpis }) {
  // Impressions / Clicks / CTR ONLY (design rev D7) — there is no spend tile
  // and no conversions tile. Three columns at every breakpoint.
  return (
    <Row gutter={[12, 12]} data-testid="ads-kpis">
      <Col xs={8} sm={8}>
        <Kpi label="Impressions" value={formatCount(kpis.impressions)} />
      </Col>
      <Col xs={8} sm={8}>
        <Kpi label="Clicks" value={formatCount(kpis.clicks)} />
      </Col>
      <Col xs={8} sm={8}>
        <Kpi label="Click-through rate" value={formatPercent(kpis.ctr)} />
      </Col>
    </Row>
  );
}

function TrendChart({ trend }: { trend: AdsTrendPoint[] }) {
  // Impressions on the left axis; clicks on the right. NO spend or conversions
  // axis or series (design rev D7) — the trend is volume + engagement only.
  return (
    <div className="ads-chart" data-testid="ads-trend-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={trend} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11 }} stroke="#9aa" />
          <YAxis yAxisId="vol" tick={{ fontSize: 11 }} stroke="#9aa" allowDecimals={false} />
          <YAxis yAxisId="eng" orientation="right" tick={{ fontSize: 11 }} stroke="#9aa" allowDecimals={false} />
          <Tooltip labelFormatter={(label: unknown) => shortDate(String(label))} />
          <Legend />
          <Line yAxisId="vol" type="monotone" dataKey="impressions" name="Impressions" stroke="#325CEB" strokeWidth={2} dot={false} />
          <Line yAxisId="eng" type="monotone" dataKey="clicks" name="Clicks" stroke="#10b981" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function EnrollmentPanel({ title, body }: { title: string; body: string }) {
  return (
    <div className="ads-panel" data-testid="ads-panel">
      <div className="ads-panel-title">{title}</div>
      <div className="ads-panel-body">{body}</div>
    </div>
  );
}

function CalendarGlyph() {
  return (
    <svg className="ads-range-glyph" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <rect x="1.5" y="2.5" width="13" height="12" rx="1.5" fill="none" stroke="currentColor" />
      <line x1="1.5" y1="6" x2="14.5" y2="6" stroke="currentColor" />
      <line x1="5" y1="1" x2="5" y2="4" stroke="currentColor" />
      <line x1="11" y1="1" x2="11" y2="4" stroke="currentColor" />
    </svg>
  );
}

/**
 * The Google Ads style range control: a bordered trigger showing the active
 * selection, opening a menu of the five presets plus a custom calendar whose
 * pickable days are pinned to the served data window. Selecting anything is
 * pure page state — no request is ever issued from here.
 */
function RangeSelector({
  selection,
  window,
  resolvedText,
  onSelect,
}: {
  selection: AdsRangeSelection;
  window: { from: string; to: string };
  resolvedText: string | null;
  onSelect: (selection: AdsRangeSelection) => void;
}) {
  const [open, setOpen] = useState(false);
  // Resolved at render time (not module scope) so the test-side module mock
  // is honored the same way it is for every other antd component.
  const { RangePicker } = DatePicker;

  const label =
    selection.kind === "preset"
      ? ADS_RANGE_PRESETS.find((p) => p.key === selection.key)?.label ?? "Date range"
      : "Custom";

  // Typed from the prop, not the app's Dayjs: yarn.lock carries two dayjs
  // copies (app 1.11.10, antd 1.11.21) whose Dayjs interfaces don't unify, so
  // an app-Dayjs parameter fails the strict deploy build with TS2322.
  const outsideWindow: NonNullable<
    React.ComponentProps<typeof RangePicker>["disabledDate"]
  > = (day) => {
    const iso = day.format("YYYY-MM-DD");
    return iso < window.from || iso > window.to;
  };

  // A plain Dayjs array, not a fixed 2-tuple type annotation: the tuple
  // type-annotation syntax crashes react-scripts 3.4's pinned
  // @typescript-eslint/parser 2.34 during the CI ESLint pass (build fails with
  // "Cannot read properties of undefined (reading 'map')"). `as never` on the
  // value prop bridges to antd's RangeValue without reintroducing tuple syntax.
  const pickerValue: Dayjs[] | null =
    selection.kind === "custom"
      ? [dayjs(selection.range.from), dayjs(selection.range.to)]
      : null;

  const menu = (
    <div className="ads-range-menu">
      <div className="ads-range-presets">
        {ADS_RANGE_PRESETS.map((preset) => {
          const active = selection.kind === "preset" && selection.key === preset.key;
          return (
            <button
              key={preset.key}
              type="button"
              className={active ? "ads-range-preset ads-range-preset-active" : "ads-range-preset"}
              data-testid={`ads-range-preset-${preset.key}`}
              onClick={() => {
                onSelect({ kind: "preset", key: preset.key });
                setOpen(false);
              }}
            >
              {preset.label}
            </button>
          );
        })}
      </div>
      <div className="ads-range-custom">
        <div className="ads-range-custom-label">Custom</div>
        <RangePicker
          size="small"
          allowClear={false}
          value={pickerValue as never}
          disabledDate={outsideWindow}
          onChange={(values) => {
            if (!values || !values[0] || !values[1]) return;
            onSelect({
              kind: "custom",
              range: { from: values[0].format("YYYY-MM-DD"), to: values[1].format("YYYY-MM-DD") },
            });
            setOpen(false);
          }}
        />
      </div>
    </div>
  );

  return (
    <Popover
      content={menu}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      rootClassName="ads-range-popover"
    >
      <button
        type="button"
        className="ads-range-trigger"
        data-testid="ads-range-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <CalendarGlyph />
        <span className="ads-range-trigger-label">{label}</span>
        {resolvedText ? <span className="ads-range-trigger-dates">{resolvedText}</span> : null}
        <span className="ads-range-caret" aria-hidden="true">
          ▾
        </span>
      </button>
    </Popover>
  );
}

export default function Ads({ todayISO }: { todayISO?: string } = {}) {
  const [view, setView] = useState<AdsViewState>({ kind: "loading" });
  const [selection, setSelection] = useState<AdsRangeSelection>({ kind: "preset", key: "last30" });

  useEffect(() => {
    let cancelled = false;
    // Shared, deduped fetch (also used by HomePage for tab visibility). Never
    // throws: a network failure resolves to status 0 -> unavailable.
    loadAdsDashboard().then(({ status, body }) => {
      if (!cancelled) setView(interpretAdsResponse(status, body));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (view.kind === "loading") {
    return (
      <div className="ads-page ads-center" data-testid="ads-loading">
        <Spin />
      </div>
    );
  }

  if (view.kind === "notEnrolled") {
    return (
      <div className="ads-page" data-testid="ads-not-enrolled">
        <EnrollmentPanel
          title="Ads aren't set up for your account yet"
          body="Once Alauda launches a campaign for you, your ad performance shows up here."
        />
      </div>
    );
  }

  if (view.kind === "unavailable") {
    return (
      <div className="ads-page" data-testid="ads-unavailable">
        <EnrollmentPanel
          title="Ads are temporarily unavailable"
          body="We couldn't load your ad performance right now. Please check back shortly."
        />
      </div>
    );
  }

  const { campaign, freshness, ads } = view.dashboard;
  const fresh = freshnessLine(freshness);

  const anchor = todayISO ?? localTodayISO();
  const requestedRange =
    selection.kind === "preset" ? presetDateRange(selection.key, anchor) : selection.range;
  const rangeView = deriveRangeView(view.dashboard, requestedRange);
  const resolvedText = rangeView.range ? formatRangeText(rangeView.range) : null;
  const selectionLabel =
    selection.kind === "preset"
      ? ADS_RANGE_PRESETS.find((p) => p.key === selection.key)?.label ?? "Selected range"
      : "Custom range";

  return (
    <section className="ads-page" data-testid="ads-dashboard">
      <header className="ads-header">
        <div className="ads-header-main">
          <h1 className="ads-title">Ads</h1>
          <p className="ads-subtitle">
            {campaign.name}
            <span className="ads-status"> · {campaign.status}</span>
          </p>
          {fresh.text ? (
            <p className={fresh.tone === "warn" ? "ads-freshness ads-freshness-warn" : "ads-freshness"}>{fresh.text}</p>
          ) : null}
        </div>
        <RangeSelector
          selection={selection}
          window={view.dashboard.window}
          resolvedText={resolvedText}
          onSelect={setSelection}
        />
      </header>

      {/* Labeled as the authoritative totals (D9): ad rows below are
          informational and never promise to sum to these tiles. The tiles
          follow the selected range; the FULL window shows the server totals
          verbatim, a narrower slice is re-summed client-side. */}
      <div className="ads-section-title">
        Campaign totals
        {resolvedText && !rangeView.isFullWindow ? (
          <span className="ads-section-title-note"> · {resolvedText}</span>
        ) : null}
      </div>
      <KpiTiles kpis={rangeView.kpis} />

      <div className="ads-section">
        <div className="ads-section-title">{resolvedText ?? selectionLabel}</div>
        {rangeView.trend.length > 0 ? (
          <TrendChart trend={rangeView.trend} />
        ) : (
          <p className="ads-muted">No activity for this date range yet.</p>
        )}
      </div>

      <AdsSection ads={ads} />
    </section>
  );
}
